import { ChangeDetectorRef, Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { caretDownOutline, caretUpOutline, funnelOutline, swapVerticalOutline } from 'ionicons/icons';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { ManagedPlatformUser, PlatformRole, PlanStatus, SubscriptionPlan } from '../../models/platform-access.models';
import {
  AdminApiError,
  AdminService,
  AdminUserListState,
  CreateManagedUserRequest,
  UpdateManagedUserRequest
} from '../../services/admin.service';
import { AuthService, User } from '../../services/auth.service';
import { PlatformModeService } from '../../services/platform-mode.service';
import { ForegroundRefreshService } from '../../services/foreground-refresh.service';

type UserModalMode = 'create' | 'edit' | null;
type ActivityFilter = 'all' | 'active' | 'inactive';
type VerificationFilter = 'all' | 'verified' | 'unverified';
type RoleFilter = PlatformRole | 'all';
type PlanStatusFilter = PlanStatus | 'all';
type SortDirection = 'asc' | 'desc';
type SortColumn = 'email' | 'platformRole' | 'plan' | 'planStatus' | 'isActive' | 'emailVerified' | 'createdAt';

interface UserFilters {
  search: string;
  role: RoleFilter;
  planCode: string;
  activity: ActivityFilter;
  planStatus: PlanStatusFilter;
  emailVerified: VerificationFilter;
}

@Component({
  selector: 'app-admin-users-page',
  standalone: true,
  imports: [FormsModule, DatePipe, IonIcon, ApiLoadErrorComponent, InfoTooltipComponent],
  templateUrl: './admin-users-page.component.html',
  styleUrls: ['./admin-users-page.component.scss']
})
export class AdminUsersPageComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly planStatuses: PlanStatus[] = ['active', 'trial', 'past_due', 'suspended'];
  readonly filterDefaults: UserFilters = {
    search: '',
    role: 'all',
    planCode: 'all',
    activity: 'all',
    planStatus: 'all',
    emailVerified: 'all',
  };

  isInitialLoading = true;
  hasInitialLoadError = false;
  isCreating = false;
  isSaving = false;
  filtersOpen = true;
  activeModal: UserModalMode = null;
  sortColumn: SortColumn = 'createdAt';
  sortDirection: SortDirection = 'desc';

  currentUser: User | null = null;
  users: ManagedPlatformUser[] = [];
  roles: PlatformRole[] = [];
  plans: SubscriptionPlan[] = [];
  selectedUserPublicId: string | null = null;
  filters: UserFilters = { ...this.filterDefaults };

  createMessage = '';
  createError = '';
  saveMessage = '';
  saveError = '';

  createForm: CreateManagedUserRequest = this.getDefaultCreateForm();
  editForm: UpdateManagedUserRequest = this.getDefaultEditForm();
  private isBackgroundRefreshInFlight = false;

  constructor(
    private readonly adminService: AdminService,
    private readonly authService: AuthService,
    private readonly foregroundRefresh: ForegroundRefreshService,
    public readonly platformModeService: PlatformModeService
  ) {
    addIcons({ funnelOutline, swapVerticalOutline, caretUpOutline, caretDownOutline });
    this.currentUser = this.authService.currentUser;
  }

  ngOnInit(): void {
    this.filtersOpen = !this.isCompactMode;
    this.loadData();
    this.foregroundRefresh.watch(5000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadData(undefined, true));
  }

  get isCompactMode(): boolean {
    return this.platformModeService.isCompactViewport();
  }

  get selectedUser(): ManagedPlatformUser | null {
    return this.users.find((user) => user.publicId === this.selectedUserPublicId) ?? null;
  }

  get editingOwnAccount(): boolean {
    return this.selectedUser?.publicId === this.currentUser?.publicId;
  }

  get filteredUsers(): ManagedPlatformUser[] {
    const searchTerm = this.filters.search.trim().toLowerCase();

    return [...this.users]
      .filter((user) => {
        if (searchTerm) {
          const haystack = [user.email, user.name ?? '', user.nickname ?? '']
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(searchTerm)) {
            return false;
          }
        }

        if (this.filters.role !== 'all' && user.platformRole !== this.filters.role) {
          return false;
        }

        if (this.filters.planCode !== 'all' && user.plan.code !== this.filters.planCode) {
          return false;
        }

        if (this.filters.activity === 'active' && !user.isActive) {
          return false;
        }

        if (this.filters.activity === 'inactive' && user.isActive) {
          return false;
        }

        if (this.filters.planStatus !== 'all' && user.plan.status !== this.filters.planStatus) {
          return false;
        }

        if (this.filters.emailVerified === 'verified' && !user.emailVerified) {
          return false;
        }

        if (this.filters.emailVerified === 'unverified' && user.emailVerified) {
          return false;
        }

        return true;
      })
      .sort((left, right) => {
        return this.compareUsers(left, right);
      });
  }

  get hasActiveFilters(): boolean {
    return JSON.stringify(this.filters) !== JSON.stringify(this.filterDefaults);
  }

  get filtersToggleLabel(): string {
    return this.filtersOpen ? 'Hide filters' : 'Show filters';
  }

  get activeModalTitle(): string {
    return this.activeModal === 'create' ? 'New user' : 'Edit user';
  }

  get currentSortLabel(): string {
    switch (this.sortColumn) {
      case 'email':
        return 'email';
      case 'platformRole':
        return 'role';
      case 'plan':
        return 'plan';
      case 'planStatus':
        return 'plan status';
      case 'isActive':
        return 'activity';
      case 'emailVerified':
        return 'verification';
      case 'createdAt':
      default:
        return 'created date';
    }
  }

  get filtersToggleIcon(): string {
    return 'funnel-outline';
  }

  loadData(preserveUserPublicId?: string | null, background = false): void {
    if (background) {
      if (this.isBackgroundRefreshInFlight || this.isInitialLoading || this.isCreating || this.isSaving || this.activeModal) return;
      this.isBackgroundRefreshInFlight = true;
    } else {
      this.isInitialLoading = true;
      this.hasInitialLoadError = false;
    }

    this.adminService.getUsers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state: AdminUserListState) => {
          this.runInAngular(() => {
            this.users = state.users;
            this.roles = state.roles;
            this.plans = state.plans;

            if (preserveUserPublicId !== undefined) {
              this.selectedUserPublicId = preserveUserPublicId;
            }

            if (!this.filters.planCode || !this.planExists(this.filters.planCode)) {
              this.filters.planCode = 'all';
            }

            if (!background) this.isInitialLoading = false;
            this.isBackgroundRefreshInFlight = false;
          });
        },
        error: () => {
          this.runInAngular(() => {
            if (!background) {
              this.hasInitialLoadError = true;
              this.isInitialLoading = false;
            }
            this.isBackgroundRefreshInFlight = false;
          });
        }
      });
  }

  toggleFilters(): void {
    this.filtersOpen = !this.filtersOpen;
  }

  closeFilters(): void {
    this.filtersOpen = false;
  }

  resetFilters(): void {
    this.filters = { ...this.filterDefaults };
  }

  toggleSort(column: SortColumn): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.sortColumn = column;
    this.sortDirection = column === 'createdAt' ? 'desc' : 'asc';
  }

  sortIcon(column: SortColumn): string {
    if (this.sortColumn !== column) {
      return 'swap-vertical-outline';
    }

    return this.sortDirection === 'asc' ? 'caret-up-outline' : 'caret-down-outline';
  }

  isSortedColumn(column: SortColumn): boolean {
    return this.sortColumn === column;
  }

  openCreateModal(): void {
    if (this.isCompactMode) {
      this.filtersOpen = false;
    }
    this.activeModal = 'create';
    this.createError = '';
    this.createForm = this.getDefaultCreateForm();
  }

  openEditModal(user: ManagedPlatformUser): void {
    if (this.isCompactMode) {
      this.filtersOpen = false;
    }
    this.selectedUserPublicId = user.publicId;
    this.populateEditForm(user);
    this.saveError = '';
    this.activeModal = 'edit';
  }

  closeModal(): void {
    this.activeModal = null;
    this.createError = '';
    this.saveError = '';
    this.isCreating = false;
    this.isSaving = false;
  }

  createUser(): void {
    this.isCreating = true;
    this.createError = '';
    this.createMessage = '';

    this.adminService.createUser(this.createForm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user) => {
          this.runInAngular(() => {
            this.createMessage = `${user.email} was created successfully.`;
            this.isCreating = false;
            this.closeModal();
            this.loadData(user.publicId);
          });
        },
        error: (error: AdminApiError) => {
          this.runInAngular(() => {
            this.createError = error.message;
            this.isCreating = false;
          });
        }
      });
  }

  saveSelectedUser(): void {
    const selectedUser = this.selectedUser;
    if (!selectedUser || this.editingOwnAccount) {
      return;
    }

    this.isSaving = true;
    this.saveError = '';
    this.saveMessage = '';

    this.adminService.updateUser(selectedUser.publicId, this.editForm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user) => {
          this.runInAngular(() => {
            this.saveMessage = `${user.email} was updated successfully.`;
            this.isSaving = false;
            this.closeModal();
            this.loadData(user.publicId);
          });
        },
        error: (error: AdminApiError) => {
          this.runInAngular(() => {
            this.saveError = error.message;
            this.isSaving = false;
          });
        }
      });
  }

  private populateEditForm(user: ManagedPlatformUser): void {
    this.editForm = {
      name: user.name,
      nickname: user.nickname,
      role: user.platformRole,
      planCode: user.plan.code,
      planStatus: user.plan.status,
      isActive: user.isActive
    };
  }

  private getDefaultCreateForm(): CreateManagedUserRequest {
    return {
      email: '',
      password: '',
      name: '',
      role: 'member',
      planCode: this.plans[0]?.code ?? 'free',
      isActive: true
    };
  }

  private getDefaultEditForm(): UpdateManagedUserRequest {
    return {
      name: '',
      nickname: '',
      role: 'member',
      planCode: this.plans[0]?.code ?? 'free',
      planStatus: 'active',
      isActive: true
    };
  }

  private planExists(planCode: string): boolean {
    if (planCode === 'all') {
      return true;
    }

    return this.plans.some((plan) => plan.code === planCode);
  }

  private compareUsers(left: ManagedPlatformUser, right: ManagedPlatformUser): number {
    const direction = this.sortDirection === 'asc' ? 1 : -1;
    const leftValue = this.getSortValue(left, this.sortColumn);
    const rightValue = this.getSortValue(right, this.sortColumn);

    if (leftValue < rightValue) {
      return -1 * direction;
    }

    if (leftValue > rightValue) {
      return 1 * direction;
    }

    return 0;
  }

  private getSortValue(user: ManagedPlatformUser, column: SortColumn): number | string {
    switch (column) {
      case 'email':
        return user.email.toLowerCase();
      case 'platformRole':
        return this.roleRank(user.platformRole);
      case 'plan':
        return user.plan.name.toLowerCase();
      case 'planStatus':
        return user.plan.status;
      case 'isActive':
        return user.isActive ? 1 : 0;
      case 'emailVerified':
        return user.emailVerified ? 1 : 0;
      case 'createdAt':
        return new Date(user.createdAt).getTime();
    }
  }

  private roleRank(role: PlatformRole): number {
    switch (role) {
      case 'owner':
        return 0;
      case 'admin':
        return 1;
      default:
        return 2;
    }
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}
